/**
 * What the error and loading screens say, in English and Spanish.
 *
 * Kept beside the components rather than in the lazily loaded catalogs
 * (`i18n/locales/*`) on purpose: these screens are shown when something failed
 * to load, a catalog chunk included, so their words must already be in the
 * bundle that draws them. The language is still i18n's: `useErrorCopy` reads
 * the active language and re-renders on a switch.
 */
import i18next from "i18next";
import { useTranslation } from "react-i18next";

export interface ErrorCopy {
  pageTitle: string;
  pageBody: string;
  contentTitle: string;
  contentBody: string;
  sectionTitle: string;
  sectionBody: string;
  staleTitle: string;
  staleBody: string;
  notFoundTitle: string;
  notFoundBody: string;
  tryAgain: string;
  reload: string;
  goHome: string;
  details: string;
  copy: string;
  copied: string;
  copyFailed: string;
  toastTitle: string;
  toastBody: string;
  retry: string;
  reloading: string;
  reconnectTitle: string;
  reconnectBody: string;
  tryNow: string;
  loading: string;
}

const en: ErrorCopy = {
  pageTitle: "Something went wrong",
  pageBody: "The app ran into a problem it couldn't recover from. Try again, or reload the page.",
  contentTitle: "This page ran into a problem",
  contentBody: "The rest of the app still works. Try again, or reload if it keeps happening.",
  sectionTitle: "This part didn't load",
  sectionBody: "The rest of the page still works.",
  staleTitle: "A newer version of the app is available",
  staleBody: "Reload to get it. Anything you've already saved is kept.",
  notFoundTitle: "This page doesn't exist",
  notFoundBody: "The link may be old, or the page may have moved.",
  tryAgain: "Try again",
  reload: "Reload",
  goHome: "Go to home",
  details: "Details",
  copy: "Copy",
  copied: "Copied",
  copyFailed: "Couldn't copy",
  toastTitle: "Couldn't refresh",
  toastBody: "Showing the last data that loaded.",
  retry: "Retry",
  reloading: "Reloading…",
  reconnectTitle: "Reconnecting…",
  reconnectBody: "The platform isn't answering right now. This page carries on by itself as soon as it does.",
  tryNow: "Try now",
  loading: "Loading…",
};

const es: ErrorCopy = {
  pageTitle: "Algo salió mal",
  pageBody: "La aplicación encontró un problema del que no pudo recuperarse. Vuelve a intentarlo o recarga la página.",
  contentTitle: "Esta página tuvo un problema",
  contentBody: "El resto de la aplicación sigue funcionando. Vuelve a intentarlo o recarga si se repite.",
  sectionTitle: "Esta parte no se cargó",
  sectionBody: "El resto de la página sigue funcionando.",
  staleTitle: "Hay una versión más reciente de la aplicación",
  staleBody: "Recarga para usarla. Lo que ya guardaste se conserva.",
  notFoundTitle: "Esta página no existe",
  notFoundBody: "El enlace puede ser antiguo, o la página se movió.",
  tryAgain: "Reintentar",
  reload: "Recargar",
  goHome: "Ir al inicio",
  details: "Detalles",
  copy: "Copiar",
  copied: "Copiado",
  copyFailed: "No se pudo copiar",
  toastTitle: "No se pudo actualizar",
  toastBody: "Se muestran los últimos datos cargados.",
  retry: "Reintentar",
  reloading: "Recargando…",
  reconnectTitle: "Reconectando…",
  reconnectBody: "La plataforma no responde en este momento. La página seguirá sola en cuanto vuelva.",
  tryNow: "Intentar ahora",
  loading: "Cargando…",
};

const COPY = { en, es } as const;
const LOCALE = { en: "en-GB", es: "es-419" } as const;

type Lang = keyof typeof COPY;

const langOf = (code: string | undefined): Lang => (code?.toLowerCase().startsWith("es") ? "es" : "en");

/** The copy for the language i18n is in now (for code outside React, e.g. the query client's toasts). */
export function errorCopyNow(): ErrorCopy {
  return COPY[langOf(i18next.resolvedLanguage ?? i18next.language)];
}

/** The copy for the active language, re-rendered on a language switch. */
export function useErrorCopy(): ErrorCopy & { locale: string } {
  const { i18n } = useTranslation();
  const lang = langOf(i18n.resolvedLanguage ?? i18n.language);
  return { ...COPY[lang], locale: LOCALE[lang] };
}
